import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Badge, Button, Card, ErrorText, Field, Input, Select, Spinner, StatTile, Table, EmptyState } from '../../components/ui'
import { clp, formatDateShort } from '../../utils/format'
import type { LedgerEntry, LedgerSummary } from '../../types/api'

function monthRange() {
  const from = new Date()
  from.setDate(1)
  from.setHours(0, 0, 0, 0)
  const to = new Date(from)
  to.setMonth(to.getMonth() + 1)
  return { from: from.toISOString(), to: to.toISOString() }
}

export function ContabilidadPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const isAccountant = user?.role === 'ACCOUNTANT'
  const { from, to } = monthRange()
  const [error, setError] = useState('')
  const [form, setForm] = useState({ type: 'EXPENSE', categoryId: '', amount: '', description: '' })

  const summary = useQuery({
    queryKey: ['ledger', 'summary'],
    queryFn: async () => (await api.get<LedgerSummary>('/accounting/summary', { params: { from, to } })).data,
  })
  const entries = useQuery({
    queryKey: ['ledger', 'entries'],
    queryFn: async () => (await api.get<LedgerEntry[]>('/accounting/ledger')).data,
  })
  const categories = useQuery({
    queryKey: ['ledger', 'categories'],
    queryFn: async () => (await api.get<Array<{ id: string; name: string; type: string }>>('/accounting/ledger/categories')).data,
  })

  const createEntry = useMutation({
    mutationFn: async () =>
      (
        await api.post('/accounting/ledger', {
          type: form.type,
          categoryId: form.categoryId,
          amount: Number(form.amount),
          description: form.description,
        })
      ).data,
    onSuccess: () => {
      setForm({ type: 'EXPENSE', categoryId: '', amount: '', description: '' })
      setError('')
      queryClient.invalidateQueries({ queryKey: ['ledger'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  if (summary.isLoading) return <Spinner />
  const s = summary.data

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-ink">Contabilidad</h1>

      <div className="grid grid-cols-3 gap-3">
        <StatTile label="Ingresos del mes" value={clp(s?.income ?? 0)} accent="brand" />
        <StatTile label="Egresos del mes" value={clp(s?.expense ?? 0)} accent="danger" />
        <StatTile label="Balance" value={clp(s?.balance ?? 0)} accent={s && s.balance >= 0 ? 'success' : 'danger'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Por categoría (mes actual)">
          {s && Object.keys(s.byCategory).length > 0 ? (
            <ul className="space-y-1.5 text-sm">
              {Object.entries(s.byCategory).map(([name, cat]) => (
                <li key={name} className="flex justify-between gap-2">
                  <span className="truncate">{name}</span>
                  <span className={`shrink-0 font-medium ${cat.type === 'INCOME' ? 'text-brand-700' : 'text-red-600'}`}>
                    {cat.type === 'INCOME' ? '+' : '−'}{clp(cat.total)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState message="Sin movimientos este mes." />
          )}
        </Card>

        <Card title="Movimientos" className="lg:col-span-2">
          {entries.isLoading ? (
            <Spinner />
          ) : (
            <Table headers={['Fecha', 'Descripción', 'Categoría', 'Monto']}>
              {(entries.data ?? []).slice(0, 20).map((e) => (
                <tr key={e.id}>
                  <td className="px-2 py-2">{formatDateShort(e.entryDate)}</td>
                  <td className="px-2 py-2">{e.description}</td>
                  <td className="px-2 py-2"><Badge>{e.category.name}</Badge></td>
                  <td className={`px-2 py-2 font-medium ${e.type === 'INCOME' ? 'text-brand-700' : 'text-red-600'}`}>
                    {e.type === 'INCOME' ? '+' : '−'}{clp(e.amount)}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>

      {isAccountant && (
        <Card title="Registrar movimiento manual">
          <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Field label="Tipo">
              <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, categoryId: '' })}>
                <option value="EXPENSE">Egreso</option>
                <option value="INCOME">Ingreso</option>
              </Select>
            </Field>
            <Field label="Categoría">
              <Select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                <option value="">Selecciona…</option>
                {(categories.data ?? [])
                  .filter((c) => c.type === form.type)
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
              </Select>
            </Field>
            <Field label="Monto CLP">
              <Input type="number" min={1} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </Field>
            <Field label="Descripción">
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Button
              disabled={!form.categoryId || !form.amount || form.description.length < 3 || createEntry.isPending}
              onClick={() => createEntry.mutate()}
            >
              Registrar
            </Button>
          </div>
          <div className="mt-2"><ErrorText>{error}</ErrorText></div>
        </Card>
      )}
    </div>
  )
}
